import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Checkbox } from '@atlaskit/checkbox';
import { FormValues } from '../../../types/ChampionApplicationFormTypes';

const PRIVACY_POLICY_URL = 'https://www.atlassian.com/legal/privacy-policy#what-this-policy-covers';
const NDA_URL =
  'https://atlassian--uat.sandbox.my.site.com/atlassianchampions/resource/1743746183000/ChampionsNDA';

type FieldErrors = {
  agreePrivacy?: string;
  agreeNda?: string;
};

type Props = {
  values: FormValues;
  updateField: <K extends keyof FormValues>(key: K, value: FormValues[K]) => void;
  errors?: FieldErrors;
};

export const AgreementsSection: React.FC<Props> = ({ values, updateField, errors }) => {
  return (
    <div css={sectionBoxStyles}>
      <div css={controlTopStyles}>
        <div
          css={agreementRowStyles}
          className={
            errors?.agreePrivacy
              ? 'champion-form-agreements champion-form-agreements-privacy--error'
              : 'champion-form-agreements'
          }
        >
          <div css={checkboxRowStyles} className="champion-form-agreements-checkbox-row">
            <abbr css={requiredStyles} title="required" aria-hidden="true">
              *
            </abbr>
            <Checkbox
              name="agree_privacy"
              label={
                <>
                  By submitting this form I agree to{' '}
                  <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" css={inlineLinkStyles}>
                    Atlassian Privacy Policy
                  </a>
                </>
              }
              isChecked={values.agreePrivacy}
              onChange={() => updateField('agreePrivacy', !values.agreePrivacy)}
            />
          </div>
        </div>

        <div
          css={agreementRowTopStyles}
          className={
            errors?.agreeNda
              ? 'champion-form-agreements champion-form-agreements-nda--error'
              : 'champion-form-agreements'
          }
        >
          <div css={checkboxRowStyles} className="champion-form-agreements-checkbox-row">
            <abbr css={requiredStyles} title="required" aria-hidden="true">
              *
            </abbr>
            <Checkbox
              name="agree_nda"
              label={
                <>
                  By checking this box, I acknowledge and agree that I am electronically signing the{' '}
                  <a href={NDA_URL} target="_blank" rel="noopener noreferrer" css={inlineLinkStyles}>
                    Non-Disclosure Agreement (NDA)
                  </a>
                  {' '}and that this electronic signature is the legal equivalent of my manual signature on the NDA. NDAs allow Atlassian Champions to receive early product news and access to feedback opportunities.
                </>
              }
              isChecked={values.agreeNda}
              onChange={() => updateField('agreeNda', !values.agreeNda)}
            />
          </div>
        </div>
      </div>

      {(errors?.agreePrivacy || errors?.agreeNda) && (
        <div css={errorStyles}>
          {errors.agreePrivacy || errors.agreeNda}
        </div>
      )}
    </div>
  );
};

const sectionBoxStyles = css({
  marginBottom: '1rem',
});

const controlTopStyles = css({
  marginTop: token('space.050'),
});

const checkboxRowStyles = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '0.25rem',
});

const agreementRowStyles = css({
  display: 'flex',
  flexDirection: 'column',
});

const agreementRowTopStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: token('space.050'),
});

const requiredStyles = css({
  color: 'var(--champion-form-error)',
  textDecoration: 'none',
  marginRight: '0.125rem',
});

const inlineLinkStyles = css({
  color: 'var(--champion-form-blue)',
  textDecoration: 'underline',
});

const errorStyles = css({
  color: 'var(--champion-form-error)',
  fontFamily: 'sans-serif',
  fontSize: '0.75rem',
  fontWeight: 400,
  marginTop: token('space.050'),
});
